/**
 * LOGIBUS — Driver offline queue (IndexedDB).
 *
 * Enqueues failed POSTs (scans, GPS pings) when network is unreachable,
 * and replays them when the browser comes back online. Uses raw IndexedDB
 * to avoid an extra dependency.
 *
 * Public API:
 *   - enqueuedPost(endpoint, body, opts)  — POST that auto-falls-back to queue
 *   - useOfflineQueue()                   — React hook returning {pending, online, flushNow}
 */

import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const DB_NAME = "logibus_offline_v1";
const STORE = "queue";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbAdd(item) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).add(item);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

async function dbAll() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

async function dbDelete(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

async function dbCount() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

let _flushing = false;
const listeners = new Set();
function emit() { listeners.forEach((cb) => cb()); }

function isNetworkError(err) {
  if (!err) return false;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (err.code === "ERR_NETWORK") return true;
  if (err.message && /network|failed to fetch|offline/i.test(err.message)) return true;
  return false;
}

export async function enqueuedPost(endpoint, body, opts = {}) {
  try {
    return await api.post(endpoint, body, opts);
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    await dbAdd({ endpoint, body, ts: Date.now() });
    emit();
    return { queued: true, endpoint };
  }
}

export async function flushQueue() {
  if (_flushing) return;
  _flushing = true;
  try {
    const items = await dbAll();
    for (const it of items) {
      try {
        await api.post(it.endpoint, it.body);
        await dbDelete(it.id);
      } catch (err) {
        if (isNetworkError(err)) break; // Stop, still offline
        // Non-network error: drop the item to avoid a poison-pill loop
        await dbDelete(it.id);
      }
    }
  } finally {
    _flushing = false;
    emit();
  }
}

export function useOfflineQueue() {
  const [pending, setPending] = useState(0);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  const refresh = useCallback(async () => {
    try { setPending(await dbCount()); } catch { setPending(0); }
  }, []);

  useEffect(() => {
    refresh();
    listeners.add(refresh);
    const onOnline = () => { setOnline(true); flushQueue(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const t = setInterval(() => { if (navigator.onLine) flushQueue(); }, 20000);
    return () => {
      listeners.delete(refresh);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      clearInterval(t);
    };
  }, [refresh]);

  return { pending, online, flushNow: flushQueue };
}
